import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { ICreateReportDTO, IReportDetailsDTO, IUpdateReportDTO, ReportType } from '../../../../../api/reports.dao';
import { MAX_REPORT_FILE_SIZE, ONE_MIB, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { APPLICATION_OCTET_STREAM, SUPPORTED_REPORT_FILE_NAME_EXTENSIONS, SUPPORTED_REPORT_TYPES } from '../../../../../constants/file.constants';
import { getFileTypeFromName } from '../../../../../infrastructure/utils/file.utils';

class ReportFormDataVM {
    public defaultValues = {
        alias: '',
        assetName: '',
        assetType: ReportType.EVENT_REPORT,
        reportFile: null,
        isFormChecked: false,
    };

    @mobx.observable
    public assetMetadataId?: number;

    @mobx.observable
    public isEdit = false;

    @mobx.observable
    public alias: string = this.defaultValues.alias;

    @mobx.observable
    public assetName: string = this.defaultValues.assetName;

    @mobx.observable
    public assetType: ReportType = this.defaultValues.assetType;

    @mobx.observable
    public reportFile: File = this.defaultValues.reportFile;

    @mobx.observable
    public isFormChecked = this.defaultValues.isFormChecked;

    @mobx.observable
    public serverErrorMessage: string;

    @mobx.observable
    public validationError: string[];

    public initValues: { alias?: string; assetType?: ReportType } = {};

    constructor(private reportData?: IReportDetailsDTO) {
        if (this.reportData) {
            this.isEdit = true;
            this.assetMetadataId = this.reportData.assetMetadataId;
            this.alias = this.reportData.alias || this.defaultValues.alias;
            this.assetType = this.reportData.assetType || this.defaultValues.assetType;
            this.assetName = this.reportData.assetName || this.defaultValues.assetName;

            this.initValues.alias = this.alias;
            this.initValues.assetType = this.assetType;
        }
    }

    @mobx.action
    public setAlias = (alias: string) => {
        this.serverErrorMessage = '';
        this.alias = alias;
    };

    @mobx.action
    public setReportFile = (reportFile: File) => {
        if (!this.alias.trim() || this.alias === this.reportFile?.name) {
            this.alias = reportFile.name;
        }
        this.reportFile = reportFile;
    };

    public getReportAliasError(intl: IntlShape): string | null {
        if (this.alias.length > SHORT_PLAIN_TEXT_LENGTH) {
            return intl.formatMessage({
                id: `oe.reportName.tooLong`,
                defaultMessage: 'Too long - max length is 255 characters',
                description: 'Error message text'
            });
        }

        return null;
    }

    private isFileSupported(): boolean {
        if (SUPPORTED_REPORT_TYPES.includes(this.reportFile.type)) return true;
        if (!this.reportFile.type || this.reportFile.type === APPLICATION_OCTET_STREAM) {
            const fileTypeFromName = getFileTypeFromName(this.reportFile);
            return SUPPORTED_REPORT_FILE_NAME_EXTENSIONS.includes(fileTypeFromName);
        }

        return false;
    }

    public getReportFileError(intl: IntlShape): string | null {
        if (this.isEdit) return null;

        if (!this.reportFile) {
            return intl.formatMessage({
                id: `oe.reports.reportFileIsRequired`,
                defaultMessage: 'File is required',
                description: 'Error message in report popover'
            });
        } else if ((this.reportFile.size / ONE_MIB) > MAX_REPORT_FILE_SIZE) {
            return intl.formatMessage({
                id: `oe.reports.tooLarge`,
                defaultMessage: 'File size is greater than 20Mb. Please, upload the file below 20Mb.',
                description: 'Error message text'
            });
        } else if (!this.isFileSupported()) {
            return intl.formatMessage({
                id: `oe.reports.wrongReportType`,
                defaultMessage: 'Unsupported file type. Please, upload file with csv, doc(x), gif, html, ics, jpeg, jpg, msg, pdf, png, ppt(x), svg, txt, xls(x) or zip extensions.',
                description: 'Error message text'
            });
        }

        return null;
    }

    public isFormValid(intl: IntlShape): boolean {
        return !this.getReportAliasError(intl) && !this.getReportFileError(intl);
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    @mobx.action
    public setServerErrorMessage(message: string): void {
        this.serverErrorMessage = message;
    }

    public toApiCreateReportDTO(): ICreateReportDTO {
        return {
            alias: this.alias.trim() || this.reportFile.name,
            assetType: this.assetType,
            reportFile: this.reportFile,
            size: this.reportFile.size,
            assetName: this.reportFile.name
        };
    }

    public toApiUpdateReportDTO(): IUpdateReportDTO {
        return {
            assetMetadataId: this.assetMetadataId,
            alias: this.alias.trim() || this.assetName,
            assetType: this.assetType,
        };
    }
}

export { ReportFormDataVM };
